import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAdminApi } from '../hooks/useAdminApi';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import EntityForm from '../components/EntityForm';
import JsonImportExport from '../components/JsonImportExport';

const columns = [
  { key: 'Skin name', label: 'Skin Name', type: 'text', sortable: true },
  { key: 'Mech name', label: 'Mech', type: 'badge', sortable: true },
  { key: 'image_url', label: 'Image', type: 'thumbnail', sortable: false },
];

const AdminSkinsPage = () => {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const { post } = useAdminApi();
  const [data, setData] = useState([]);
  const [mechNames, setMechNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showImportExport, setShowImportExport] = useState(false);
  const [likes, setLikes] = useState({});

  const loadData = useCallback(async () => {
    try {
      const [skinsRes, mechsRes, likesRes] = await Promise.all([
        fetch('/legendary_skins.json'),
        fetch('/list.json'),
        fetch('/skin-likes.json')
      ]);
      const skins = await skinsRes.json();
      const mechs = await mechsRes.json();
      const likesData = await likesRes.json();
      setData(skins);
      setMechNames([...new Set(mechs.filter(m => m.type === 'mech').map(m => m.list))].sort());
      setLikes(likesData?.likes || {});
    } catch { /* empty */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const formFields = [
    { key: 'Skin name', label: 'Skin Name', type: 'text', required: true },
    { key: 'Mech name', label: 'Mech', type: 'select', required: true, options: mechNames },
    { key: 'image_url', label: 'Image', type: 'image', uploadPath: 'Legendary skins', fileNameFrom: 'Skin name', acceptTypes: ['image/png'] },
  ];

  const columnsWithLikes = [
    ...columns,
    {
      key: '_likes',
      label: 'Likes',
      type: 'number',
      sortable: false,
    },
  ];

  const dataWithLikes = data.map(s => ({
    ...s,
    image_url: '/Legendary skins/' + s['Skin name'] + '.png',
    _likes: likes[s['Skin name']] || 0
  }));

  const handleSave = async (formData) => {
    // Remove _likes from save data
    const { _likes, image_url, ...saveData } = formData;
    try {
      if (editIndex >= 0) {
        await post('/admin-update-data.php', { file: 'legendary_skins.json', action: 'edit', data: saveData, item_index: editIndex });
      } else {
        await post('/admin-update-data.php', { file: 'legendary_skins.json', action: 'add', data: saveData });
      }
      setShowForm(false);
      loadData().catch(() => {});
    } catch (err) { alert('Save failed: ' + err.message); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await post('/admin-update-data.php', { file: 'legendary_skins.json', action: 'delete', item_index: deleteTarget.index });
      setDeleteTarget(null);
      await loadData();
    } catch (err) { alert('Delete failed: ' + err.message); }
  };

  const handleImport = async (imported) => {
    try {
      await post('/admin-update-data.php', { file: 'legendary_skins.json', action: 'update', data: imported });
      setShowImportExport(false);
      await loadData();
    } catch (err) { alert('Import failed: ' + err.message); }
  };

  if (loading) return <div className={dark ? 'text-gray-400' : 'text-gray-500'}>Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className={`text-xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>Legendary Skins</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowImportExport(true)}
            className={`px-3 py-2 rounded-lg text-sm font-medium border ${dark ? 'border-[#333] text-gray-300 hover:bg-[#333]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            Import/Export
          </button>
          <button onClick={() => { setEditItem(null); setEditIndex(-1); setShowForm(true); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium text-white ${dark ? 'bg-[#FF9800] hover:bg-[#F57C00]' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
            + Add New
          </button>
        </div>
      </div>

      <DataTable
        columns={columnsWithLikes}
        data={dataWithLikes}
        filters={[]}
        onEdit={(row, index) => { setEditItem(row); setEditIndex(index); setShowForm(true); }}
        onDelete={(row, index) => setDeleteTarget({ row, index })}
      />

      <EntityForm open={showForm} title={editIndex >= 0 ? 'Edit Skin' : 'Add Skin'} fields={formFields} initialData={editItem} onSave={handleSave} onCancel={() => setShowForm(false)} />
      <ConfirmDialog open={!!deleteTarget} title="Delete Skin" message={`Delete "${deleteTarget?.row?.['Skin name']}"?`} onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      <JsonImportExport open={showImportExport} title="Legendary Skins" data={data} onImport={handleImport} onClose={() => setShowImportExport(false)} />
    </div>
  );
};

export default AdminSkinsPage;
